import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { DEFAULT_LOCALE, type AppLocale } from '@/i18n';

export type ThemeMode = 'light' | 'dark';

/**
 * Which "mode" a multi-role user is currently viewing (D-014). Freelance mode
 * shows the browse/availability/applications tabs; appointed shows masjids/
 * requests; admin shows the platform-admin tools. `null` = auto-detect based
 * on the user's actual flags (used until the user first picks in the switcher).
 */
export type ActiveMode = 'freelance' | 'appointed' | 'admin' | null;

interface UiState {
  locale: AppLocale;
  theme: ThemeMode;
  activeMode: ActiveMode;
  setLocale: (locale: AppLocale) => void;
  toggleLocale: () => void;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  setActiveMode: (mode: ActiveMode) => void;
}

function preferredTheme(): ThemeMode {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/**
 * Client/UI preferences only (instructions/02_ARCHITECTURE.md). Persisted under a
 * namespaced key. No server data or secrets here.
 */
export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      locale: DEFAULT_LOCALE,
      theme: preferredTheme(),
      activeMode: null,
      setLocale: (locale) => set({ locale }),
      toggleLocale: () => set((s) => ({ locale: s.locale === 'ar' ? 'en' : 'ar' })),
      setTheme: (theme) => set({ theme }),
      toggleTheme: () => set((s) => ({ theme: s.theme === 'light' ? 'dark' : 'light' })),
      setActiveMode: (activeMode) => set({ activeMode }),
    }),
    {
      name: 'badeel.web.ui',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
